import { supabase } from "@/integrations/supabase/client";
import {
  getNativePushState,
  isNativePushPlatform,
  optInNativePush,
  requestNativePushPermission,
  setNativePushExternalUser,
} from "@/lib/native-push";
import { isExploreAppVariant } from "@/lib/platform";
import { logger } from "@/lib/logger";

const DEVICE_ID_KEY = "buukd:explore-device-id";

export function getExploreDeviceId(): string {
  if (typeof window === "undefined") return "";

  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing?.trim()) return existing.trim();

    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return `session-${Date.now()}`;
  }
}

export function getExploreOneSignalExternalId(): string {
  return `explore:${getExploreDeviceId()}`;
}

/** Persist Explore device on booking (bundle bookings + deposit webhook). */
export async function saveExploreDeviceForBooking(confirmationCode: string): Promise<void> {
  if (!isExploreAppVariant()) return;

  const code = String(confirmationCode || "").trim();
  const deviceId = getExploreDeviceId();
  if (!code || !deviceId) return;

  try {
    const { error } = await (supabase as any).rpc("save_explore_device_for_booking", {
      p_confirmation_code: code,
      p_device_id: deviceId,
    });
    if (error) {
      logger.warn("save_explore_device_for_booking failed", { message: error.message });
    }
  } catch (error) {
    logger.warn("save_explore_device_for_booking crashed", error as Error);
  }
}

/**
 * Register this Explore install with OneSignal (external id + permission).
 * No Supabase subscription table — sends target explore:{deviceId} via edge function.
 */
export async function syncExplorePushRegistration(options?: {
  requestPermission?: boolean;
}): Promise<boolean> {
  if (!isExploreAppVariant() || !isNativePushPlatform()) return false;

  const deviceId = getExploreDeviceId();
  if (!deviceId) return false;

  try {
    await setNativePushExternalUser(getExploreOneSignalExternalId());

    let state = await getNativePushState();
    if (options?.requestPermission && !state?.permission) {
      state = await requestNativePushPermission();
    }

    if (!state?.permission) return false;

    if (!state.optedIn) {
      state = await optInNativePush();
    }

    return true;
  } catch (error) {
    logger.warn("Explore push registration failed", error as Error);
    return false;
  }
}

/** Call before booking so the customer device is on OneSignal. */
export async function ensureExplorePushReady(): Promise<boolean> {
  if (!isExploreAppVariant() || !isNativePushPlatform()) return false;
  const ok = await syncExplorePushRegistration({ requestPermission: false });
  if (ok) return true;
  return syncExplorePushRegistration({ requestPermission: true });
}

export type ExploreCustomerPushPayload = {
  title: string;
  body: string;
  url?: string;
};

/**
 * Send a OneSignal push to the Explore customer only (co.buukd.explore).
 * Never targets business push_subscriptions.
 */
export async function sendExploreCustomerPush(
  payload: ExploreCustomerPushPayload
): Promise<boolean> {
  if (!isExploreAppVariant()) return false;

  const deviceId = getExploreDeviceId();
  if (!deviceId) return false;

  try {
    const { data, error } = await supabase.functions.invoke("send-push-notification", {
      body: {
        explore_device_id: deviceId,
        explore_only: true,
        app_scheme: "co.buukd.explore",
        payload: {
          title: payload.title,
          body: payload.body,
          url: payload.url,
          data: { audience: "explore_customer" },
        },
      },
    });

    if (error) {
      logger.warn("sendExploreCustomerPush failed", { error: String(error) });
      return false;
    }

    const sent = Number((data as { sent?: number })?.sent ?? 0);
    if (sent < 1) {
      logger.warn("sendExploreCustomerPush: no device received push", { data });
    }
    return sent > 0;
  } catch (error) {
    logger.warn("sendExploreCustomerPush crashed", error as Error);
    return false;
  }
}
